import { assign, setup } from 'xstate';
import { z } from 'zod';
import type { NavigateOptions } from '@tanstack/react-router';

import { toError } from '@repo/lib/errors';
import {
  getStorageJson,
  safeSessionStorage,
  setStorageJson,
} from '@repo/lib/storage';
import { isStepValid, validateStep } from './schema';
import { authStatusSchema, storedWorkflowDataSchema } from './schemaTypes';
import type { WorkflowData } from './schemaTypes';

// Re-export types for backward compatibility
export type { WorkflowData, WorkflowItemData, ItemId } from './schemaTypes';
export type { WorkflowEvents, WorkflowContext } from './types';
import type {
  WorkflowEvents,
  WorkflowContext,
  TaskRuntimeContext,
} from './types';

export const initialTaskRuntimeContext: TaskRuntimeContext = { gift: false };
import { fetchServerState, fetchConfig, fetchOrder } from './actors';
import { workflowGuards } from './guards';
import { client } from '../graphql/client';
import { getFingerprintHeaders } from '../utils/fingerprint';
import { STORAGE_KEYS } from '../utils/constants';
import { clientLogger, syncRollbarContextFromUrl } from '../utils/logger';
import { getParams } from '../utils/searchParams';
import {
  injectRetailerTheme,
  injectFavicon,
  type HeaderColors,
} from '../utils/theme';
import { logToTitan, workflowToTitanEvent } from '../utils/titan';
import { getRetailerFromUrl } from '../utils/getRetailerFromUrl';
import { pdxEvent } from '../utils/noflake';

export const workflowMachine = setup({
  types: {
    context: {} as WorkflowContext,
    events: {} as WorkflowEvents,
  },
  actions: {
    goToStep: assign({
      currentStepIndex: ({ context, event }) => {
        const { config } = context;
        if (!config || event.type !== 'goTo') return context.currentStepIndex;

        const stepIndex = config.steps.findIndex(
          (step) => step.id === event.stepId,
        );

        const newStepIndex =
          stepIndex >= 0 ? stepIndex : context.currentStepIndex;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );

        return newStepIndex;
      },
    }),
    handleComplete: () => {
      // Clear workflow state but preserve auth for back button navigation
      safeSessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
      safeSessionStorage.removeItem(STORAGE_KEYS.DATA);
    },
    logger: ({ context, event }) => {
      const { config, currentStepIndex } = context;
      const currentStepId = config?.steps?.[currentStepIndex]?.id;
      const titanEvent = workflowToTitanEvent[event.type];
      if (!titanEvent) return;

      pdxEvent({
        event_category: 'workflow',
        event_type: titanEvent,
        event_value: currentStepId,
      });

      try {
        const extraProperties = currentStepId
          ? [{ key: 'step', value: currentStepId }]
          : undefined;
        const workflowData = context.data
          ? {
              shipping_method:
                context.data.returnMethod && 'type' in context.data.returnMethod
                  ? context.data.returnMethod.type
                  : undefined,
              payment_method:
                context.data.refund && 'method' in context.data.refund
                  ? context.data.refund.method
                  : undefined,
            }
          : undefined;

        logToTitan({
          event: titanEvent,
          properties: {
            narvar_product_context: 'returns',
            extra_properties: extraProperties,
            order_id: context.order?.orderNumber || context.orderNumber,
            cart_id: context.requestId,
            ...workflowData,
          },
        });
      } catch (err) {
        clientLogger.logError(err);
        // eslint-disable-next-line eslint(no-console)
        console.error(`Logging error: ${toError(err).message}`);
      }
    },
    moveToNextStep: assign({
      data: ({ context, event }) => {
        if (event.type !== 'next') return context.data;
        const newData: WorkflowData = {
          ...context.data,
          ...event.data,
        };
        setStorageJson(safeSessionStorage, STORAGE_KEYS.DATA, newData);
        return newData;
      },
      currentStepIndex: ({ context, event }) => {
        if (event.type !== 'next') return context.currentStepIndex;

        const { config, currentStepIndex } = context;
        if (!config) return currentStepIndex;

        // First validate with the new data
        const currentStep = config.steps[currentStepIndex];
        if (!currentStep) return currentStepIndex;

        const newData = {
          ...context.data,
          ...event.data,
        };

        const errors = validateStep(
          currentStep,
          newData,
          context.taskRuntimeContext,
        );
        const isValid = isStepValid(
          errors,
          currentStep,
          context.taskRuntimeContext,
        );

        if (!isValid) return currentStepIndex;

        // If valid, move to next step
        const newStepIndex =
          currentStepIndex < config.steps.length - 1
            ? currentStepIndex + 1
            : currentStepIndex;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );
        return newStepIndex;
      },
    }),
    moveToPrevStep: assign({
      currentStepIndex: ({ context }) => {
        const { config, currentStepIndex } = context;
        if (!config) return context.currentStepIndex;

        const newStepIndex = currentStepIndex >= 1 ? currentStepIndex - 1 : 0;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );

        return newStepIndex;
      },
    }),
    setAuthStatus: assign({
      authStatus: ({ context, event }) => {
        if (event.type !== 'updateAuthStatus') return context.authStatus;
        safeSessionStorage.setItem(STORAGE_KEYS.AUTH_STATUS, event.status);
        return event.status;
      },
    }),
    setConfig: assign({
      retailerSettings: ({ context, event }) => {
        if (event.type !== 'xstate.done.actor.getConfig') return null;

        const { retailer = '' } = context;
        const { retailerSettings } = event.output;
        const styles = retailerSettings.styles;
        const headerColors: HeaderColors = {
          backgroundColor:
            retailerSettings.features?.header?.backgroundColor ?? undefined,
          textColor: retailerSettings.features?.header?.textColor ?? undefined,
        };

        if (styles) {
          injectRetailerTheme({
            options: {
              retailer,
            },
            styles,
            headerColors,
          });
          setStorageJson(safeSessionStorage, STORAGE_KEYS.THEME, styles);
          setStorageJson(
            safeSessionStorage,
            STORAGE_KEYS.HEADER_COLORS,
            headerColors,
          );
        }

        const faviconUrl = retailerSettings.features?.assets?.favicon;
        if (faviconUrl) {
          injectFavicon(faviconUrl);
        }

        return event.output.retailerSettings;
      },
      config: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getConfig') return null;
        return event.output.config;
      },
      currentStepIndex: () => {
        const storedCurrentStep = getStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          z.number(),
        );
        return storedCurrentStep ?? 0;
      },
    }),
    setData: assign({
      data: ({ context, event }) => {
        const { data } = context;

        if (event.type !== 'updateData') return data;

        const newData: WorkflowData = {
          ...data,
          ...event.data,
        };

        setStorageJson(safeSessionStorage, STORAGE_KEYS.DATA, newData);

        return newData;
      },
    }),
    setContextFromLogin: assign({
      loginContext: ({ context, event }) => {
        if (event.type !== 'setContextFromLogin') return context.loginContext;

        const email =
          event.data?.loginContext?.email ?? context.loginContext.email;
        if (email) {
          safeSessionStorage.setItem(STORAGE_KEYS.LOGIN_EMAIL, email);
        }
        return {
          email,
          orderNumber: event.data?.loginContext?.orderNumber ?? null,
          zipCode: event.data?.loginContext?.zipCode ?? null,
        };
      },
      order: ({ event }) => {
        if (event.type !== 'setContextFromLogin') return null;

        return event.data.order;
      },
      submittedRequests: ({ event }) => {
        if (event.type !== 'setContextFromLogin') return null;

        return event.data.submittedRequests;
      },
      taskRuntimeContext: ({ context, event }) => {
        if (event.type !== 'setContextFromLogin') {
          return context.taskRuntimeContext;
        }
        const updated = {
          ...initialTaskRuntimeContext,
          ...event.data?.taskRuntimeContext,
        };
        setStorageJson(safeSessionStorage, STORAGE_KEYS.ORDER_CONTEXT, updated);
        return updated;
      },
    }),
    setOrder: assign({
      order: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getOrder' || !event.output)
          return null;

        return event.output.order;
      },
      submittedRequests: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getOrder' || !event.output)
          return null;

        return event.output.submittedRequests;
      },
    }),
    setIsSaving: assign({
      isSaving: ({ context, event }) => {
        if (event.type !== 'updateIsSaving') {
          return context.isSaving;
        }
        return event.isSaving;
      },
    }),
    setRefundAmount: assign({
      refundAmount: ({ context, event }) => {
        if (event.type !== 'setRefundAmount') {
          return context.refundAmount;
        }
        return event.refundAmount;
      },
    }),
    setContextFromUrl: assign({
      loginContext: ({ context }) => {
        syncRollbarContextFromUrl();
        const params = getParams();

        const orderNumber =
          params?.order ??
          params?.orderNumber ??
          context.loginContext.orderNumber;

        if (
          typeof orderNumber === 'string' &&
          context.orderNumber !== orderNumber
        ) {
          safeSessionStorage.clear();
          client.setHeaders(getFingerprintHeaders());
          safeSessionStorage.setItem(STORAGE_KEYS.ORDER_NUMBER, orderNumber);
        }

        return {
          email: params?.email ?? context.loginContext.email,
          orderNumber,
          zipCode: params?.zipCode ?? context.loginContext.zipCode,
        };
      },
      taskRuntimeContext: ({ context }) => {
        const params = getParams();
        const orderNumber =
          params?.order ??
          params?.orderNumber ??
          context.loginContext.orderNumber;

        if (
          typeof orderNumber === 'string' &&
          context.orderNumber !== orderNumber
        ) {
          return initialTaskRuntimeContext;
        }

        return context.taskRuntimeContext;
      },
      orderNumber: ({ context }) => {
        const params = getParams();
        return params?.order ?? params?.orderNumber ?? context.orderNumber;
      },
      retailer: ({ context }) => {
        const params = getParams();
        const retailer = params?.retailer ?? getRetailerFromUrl();

        return retailer ?? context.retailer;
      },
      shid: ({ context }) => {
        const params = getParams();
        return params?.shid ?? context.shid;
      },
      token: ({ context }) => {
        const params = getParams();
        const token = params?.token;
        if (token) {
          client.setHeader('X-Authentication', token);
          return token;
        }
        return context.token;
      },
      requestId: ({ context }) => {
        const params = getParams();
        return params?.request ?? context.requestId;
      },
    }),
    processServerState: assign({
      authStatus: 'authenticated',
      currentStepIndex: ({ context, event }) => {
        const { config } = context;

        if (event.type !== 'xstate.done.actor.getServerState' || !config) {
          return 0;
        }

        for (let i = 0; i < config.steps.length; i++) {
          const step = config.steps[i];
          if (!step) {
            return Math.max(i - 1, 0);
          }

          const errors = validateStep(
            step,
            event.output,
            context.taskRuntimeContext,
          );
          const isValid = isStepValid(errors, step, context.taskRuntimeContext);

          if (!isValid) {
            return i;
          }
        }

        return config.steps.length - 1;
      },
      data: ({ context, event }) => {
        if (event.type !== 'xstate.done.actor.getServerState') {
          return context.data;
        }
        return event.output;
      },
    }),
    setRedirectToClaimDetails: assign({
      redirectTo: ({ context }) => {
        if (
          !context.requestId ||
          !context.submittedRequests ||
          !context.orderNumber ||
          !context.retailer
        ) {
          return null;
        }
        const match = context.submittedRequests.find(
          (req) => req.id === context.requestId,
        );
        if (match) {
          return {
            to: '/$retailer/claim-details' as const,
            params: { retailer: context.retailer },
            search: {
              order: context.orderNumber,
              claimId: context.requestId,
            },
          };
        }
        return null;
      },
    }),
    setRedirectToClaimDetailsOnError: assign({
      redirectTo: ({ context }) => {
        const path = '/claim-details';

        pdxEvent({
          event_category: 'redirect',
          event_type: 'redirect',
          event_value: `invalid token redirecting to ${path}`,
        });

        const val =
          !context.requestId || !context.orderNumber || !context.retailer
            ? null
            : {
                to: `/$retailer${path}` as const,
                params: { retailer: context.retailer },
                search: {
                  order: context.orderNumber,
                  claimId: context.requestId,
                },
              };
        return val;
      },
    }),
    handleLogout: assign(({ context }) => {
      safeSessionStorage.clear();
      client.setHeaders(getFingerprintHeaders());

      return {
        retailer: context.retailer,
        shid: context.shid,
        config: null,
        retailerSettings: null,
        authStatus: 'unauthenticated' as const,
        currentStepIndex: 0,
        data: null,
        errors: {},
        order: null,
        submittedRequests: null,
        loginContext: {
          email: null,
          orderNumber: null,
          zipCode: null,
        },
        orderNumber: null,
        requestId: null,
        token: null,
        redirectTo: {
          to: '/$retailer' as const,
          params: { retailer: context.retailer },
          reloadDocument: true,
          search: true,
        } as NavigateOptions,
        refundAmount: null,
        error: null,
        orderError: null,
        isSaving: false,
        isLoading: {
          order: false,
        },
        taskRuntimeContext: initialTaskRuntimeContext,
      };
    }),
  },
  actors: {
    fetchServerState,
    fetchConfig,
    fetchOrder,
  },
  guards: workflowGuards,
}).createMachine({
  id: 'workflow',
  initial: 'checkUrl',
  entry: 'logger',
  context: {
    authStatus: authStatusSchema
      .catch('unauthenticated')
      .parse(safeSessionStorage.getItem(STORAGE_KEYS.AUTH_STATUS)),
    retailerSettings: null,
    config: null,
    currentStepIndex: 0,
    data: getStorageJson(
      safeSessionStorage,
      STORAGE_KEYS.DATA,
      storedWorkflowDataSchema.pipe(z.custom<WorkflowData>()),
    ),
    errors: {},
    isSaving: false,
    isLoading: {
      order: false,
    },
    loginContext: {
      email: safeSessionStorage.getItem(STORAGE_KEYS.LOGIN_EMAIL),
      orderNumber: null,
      zipCode: null,
    },
    order: null,
    taskRuntimeContext:
      getStorageJson(
        safeSessionStorage,
        STORAGE_KEYS.ORDER_CONTEXT,
        z.object({ gift: z.boolean() }),
      ) ?? initialTaskRuntimeContext,
    orderNumber: safeSessionStorage.getItem(STORAGE_KEYS.ORDER_NUMBER),
    requestId: null,
    retailer: null,
    shid: null,
    submittedRequests: null,
    token: safeSessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
    error: null,
    orderError: null,
    redirectTo: null,
    refundAmount: null,
  },
  states: {
    checkUrl: {
      always: {
        actions: 'setContextFromUrl',
        target: 'loadConfig',
      },
    },
    loadConfig: {
      invoke: {
        id: 'getConfig',
        src: 'fetchConfig',
        input: ({ context: { retailer } }) => ({
          retailer,
        }),
        onDone: {
          actions: 'setConfig',
          target: 'loadOrder',
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (_, event: unknown) => {
              if (
                event &&
                typeof event === 'object' &&
                'data' in event &&
                event.data instanceof Error
              ) {
                return event.data;
              }
              return new Error('Config loading failed');
            },
          }),
        },
      },
    },
    loadOrder: {
      entry: assign({
        isLoading: ({ context }) => ({ ...context.isLoading, order: true }),
      }),
      exit: assign({
        isLoading: ({ context }) => ({ ...context.isLoading, order: false }),
      }),
      invoke: {
        id: 'getOrder',
        src: 'fetchOrder',
        input: ({ context: { orderNumber, loginContext, requestId } }) => {
          const { email, zipCode } = loginContext;
          return {
            orderNumber,
            email,
            zipCode,
            requestId,
          };
        },
        onDone: [
          {
            actions: ['setOrder', 'setRedirectToClaimDetails'],
            guard: 'hasOrderNumber',
            target: 'checkAuth',
          },
          {
            actions: 'setRedirectToClaimDetails',
            target: 'checkAuth',
          },
        ],
        onError: [
          {
            guard: 'canRedirectToClaimDetails',
            actions: [
              assign({
                orderError: (_, event: unknown) => {
                  if (
                    event &&
                    typeof event === 'object' &&
                    'type' in event &&
                    event.type === 'xstate.error.actor.getOrder' &&
                    'data' in event
                  ) {
                    return event.data instanceof Error
                      ? event.data
                      : new Error(String(event.data));
                  }
                  return new Error('Unknown order error');
                },
                authStatus: () => {
                  safeSessionStorage.clear();
                  return 'unauthenticated';
                },
              }),
              'setRedirectToClaimDetailsOnError',
            ],
            target: 'ready',
          },
          {
            actions: [
              assign({
                orderError: (_, event: unknown) => {
                  if (
                    event &&
                    typeof event === 'object' &&
                    'type' in event &&
                    event.type === 'xstate.error.actor.getOrder' &&
                    'data' in event
                  ) {
                    return event.data instanceof Error
                      ? event.data
                      : new Error(String(event.data));
                  }
                  return new Error('Unknown order error');
                },
                authStatus: () => {
                  safeSessionStorage.clear();
                  return 'unauthenticated';
                },
              }),
            ],
            target: 'notAuthenticated',
          },
        ],
      },
    },
    checkAuth: {
      always: [
        {
          guard: 'isRehydrateFromServer',
          target: 'rehydrateFromServer',
        },
        {
          guard: 'isAuthenticated',
          target: 'ready',
        },
        {
          guard: 'isOrderlessAuthenticated',
          actions: assign({
            authStatus: 'authenticated',
          }),
          target: 'ready',
        },
        {
          target: 'notAuthenticated',
        },
      ],
    },
    notAuthenticated: {
      on: {
        updateAuthStatus: {
          target: 'checkAuth',
          actions: 'setAuthStatus',
        },
      },
    },
    rehydrateFromServer: {
      invoke: {
        id: 'getServerState',
        src: 'fetchServerState',
        input: ({ context: { config, orderNumber, requestId, token } }) => ({
          config,
          orderNumber,
          requestId,
          token,
        }),
        onDone: {
          actions: 'processServerState',
          target: 'ready',
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (_, event: unknown) => {
              if (
                event &&
                typeof event === 'object' &&
                'type' in event &&
                event.type === 'xstate.error.actor.getServerState' &&
                'data' in event
              ) {
                return event.data instanceof Error
                  ? event.data
                  : new Error(String(event.data));
              }
              return new Error('Unknown server state error');
            },
          }),
        },
      },
    },
    error: {
      entry: 'logger',
      type: 'final',
    },
    logout: {
      entry: 'handleLogout',
      always: {
        target: 'notAuthenticated',
      },
    },
    ready: {
      initial: 'active',
      entry: 'logger',
      states: {
        active: {
          on: {
            complete: {
              actions: ['handleComplete', 'logger'],
              target: 'complete',
            },
            goTo: {
              actions: ['goToStep', 'logger'],
            },
            logout: {
              target: '#workflow.logout',
            },
            next: {
              actions: ['moveToNextStep', 'logger'],
            },
            prev: {
              actions: ['moveToPrevStep', 'logger'],
            },
            setContextFromLogin: { actions: 'setContextFromLogin' },
            updateData: {
              actions: 'setData',
              target: 'active',
            },
            updateIsSaving: {
              actions: 'setIsSaving',
              target: 'active',
            },
            setRefundAmount: { actions: 'setRefundAmount' },
          },
        },
        complete: {
          type: 'final',
        },
      },
    },
  },
});
